import React, { useMemo, useState } from 'react';
import { Checkbox, Input, Spin, Tooltip } from 'antd';
import { CiSearch } from 'react-icons/ci';
import clsx from 'clsx';
import { PermissionsPickerVariant } from '../../../enums/permissionEnums';
import styles from './PermissionsPicker.module.scss';

export interface PermissionPrincipal {
  id: string;
  name: string;
}

export interface PermissionsPickerProps {
  users: PermissionPrincipal[];
  groups: PermissionPrincipal[];
  /** Namespaced `user-<id>` / `group-<id>` values. */
  selectedValues: string[];
  onChange: (values: string[]) => void;
  variant?: PermissionsPickerVariant;
  isLoading?: boolean;
  /** Show who has access without allowing a change. */
  readOnly?: boolean;
}

const USER_PREFIX = 'user-';
const GROUP_PREFIX = 'group-';

export const toPermissionValues = (userIds: string[], groupIds: string[]): string[] => [
  ...userIds.map((id) => `${USER_PREFIX}${id}`),
  ...groupIds.map((id) => `${GROUP_PREFIX}${id}`),
];

export const splitPermissionValues = (values: string[]) => {
  const userValues: string[] = [];
  const groupValues: string[] = [];
  for (const value of values) {
    if (value.startsWith(USER_PREFIX)) userValues.push(value.slice(USER_PREFIX.length));
    else if (value.startsWith(GROUP_PREFIX)) groupValues.push(value.slice(GROUP_PREFIX.length));
  }
  return { userValues, groupValues };
};

const matchesSearch = (principals: PermissionPrincipal[], search: string) => {
  if (!search) return principals;
  return principals.filter((principal) => principal.name.toLowerCase().includes(search));
};

export const PermissionsPicker: React.FC<PermissionsPickerProps> = ({
  users,
  groups,
  selectedValues,
  onChange,
  variant = PermissionsPickerVariant.LIGHT,
  isLoading = false,
  readOnly = false,
}) => {
  const [search, setSearch] = useState('');

  const normalizedSearch = search.trim().toLowerCase();
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const filteredUsers = useMemo(() => matchesSearch(users, normalizedSearch), [users, normalizedSearch]);
  const filteredGroups = useMemo(() => matchesSearch(groups, normalizedSearch), [groups, normalizedSearch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleToggle = (value: string) => {
    if (readOnly) return;
    const next = selectedSet.has(value) ? selectedValues.filter((selected) => selected !== value) : [...selectedValues, value];
    onChange(next);
  };

  const renderSection = (heading: string, principals: PermissionPrincipal[], prefix: string, emptyLabel: string) => (
    <>
      <p className={styles.heading}>{heading}</p>
      <div className={styles.list}>
        {principals.length === 0 ? (
          <p className={styles.empty}>{emptyLabel}</p>
        ) : (
          principals.map((principal) => {
            const value = `${prefix}${principal.id}`;
            return (
              <div key={value} className={clsx(styles.item, readOnly && styles.readOnly)} onClick={() => handleToggle(value)}>
                <Tooltip title={principal.name} placement="topLeft" mouseEnterDelay={0.7} arrow={false}>
                  <span className={styles.labelText}>{principal.name}</span>
                </Tooltip>
                <Checkbox
                  className="custom-checkbox"
                  checked={selectedSet.has(value)}
                  onChange={() => handleToggle(value)}
                  onClick={(event) => event.stopPropagation()}
                />
              </div>
            );
          })
        )}
      </div>
    </>
  );

  return (
    <div className={clsx(styles.picker, styles[variant])}>
      <Input
        className={styles.search}
        value={search}
        onChange={handleSearchChange}
        placeholder="SEARCH USERS AND GROUPS"
        aria-label="Search users and groups"
        suffix={<CiSearch className={styles.searchIcon} />}
      />
      {isLoading ? (
        <div className={styles.loading}>
          <Spin size="small" />
        </div>
      ) : (
        <>
          {renderSection('USERS', filteredUsers, USER_PREFIX, 'No users found')}
          {renderSection('GROUPS', filteredGroups, GROUP_PREFIX, 'No groups found')}
        </>
      )}
    </div>
  );
};
